import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

function money(v: number | undefined): string {
  return (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(part: number | undefined, total: number | undefined): string {
  if (!total) return '—';
  return `${(((part ?? 0) / total) * 100).toFixed(1)}%`;
}

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now.toLocaleTimeString('pt-BR');
}

const KPI_ICON = { gmv: 'payments', gmv30: 'trending_up', comissao: 'percent', assinaturas: 'autorenew', parceiros: 'groups', lojas: 'storefront' };

/** Admin · Cockpit 360° (era "Visão geral") — retrato agregado da plataforma. Consome
 *  `GET /api/admin/overview`. Redesign Lumina: os 6 KPIs principais são todos reais (GMV,
 *  comissão, assinaturas, parceiros, lojas); a seção "Sinais Avançados" no fim (antifraude,
 *  chargeback, PCI-DSS/BACEN) é do protótipo Super Admin e não existe na Storeo hoje — a
 *  plataforma usa adapter de pagamento mock, não tem WAF/HSM nem certificação real —, por
 *  isso fica marcada "exemplo" em vez de apagada. */
export function VisaoGeral() {
  const overview = useQuery({ queryKey: ['admin', 'overview'], queryFn: () => api.overview() });
  const d = overview.data;
  const funnel = d?.funnel30d;
  const clock = useClock();

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-space-xl shadow-xl">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-primary-container/10 blur-[100px] pointer-events-none" />
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end justify-between gap-space-md">
          <div className="flex flex-col gap-space-2xs">
            <div className="inline-flex items-center gap-space-2xs">
              <span className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-secondary" />
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary font-bold">PAINEL INTERNO</span>
            </div>
            <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold tracking-tight">Cockpit 360°</h1>
            <p className="font-body-sm text-body-sm text-on-surface-variant max-w-2xl">
              Retrato da plataforma hoje — parceiros, lojas, dinheiro e funil dos últimos 30 dias.
            </p>
          </div>
          <div className="flex items-center gap-space-2xs px-space-sm py-space-xs rounded-lg bg-surface-container-lowest shadow-sm self-start lg:self-auto">
            <MSym name="schedule" className="text-[18px] text-tertiary" />
            <span className="font-label-sm text-label-sm text-on-surface font-semibold font-mono">{clock}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-space-md">
        <KpiCard icon={KPI_ICON.gmv} label="GMV total" value={`R$ ${money(d?.money.gmvAllTime)}`} sub={`${d?.money.paidOrders ?? 0} pedido(s) pago(s)`} />
        <KpiCard icon={KPI_ICON.gmv30} label="GMV últimos 30d" value={`R$ ${money(d?.money.gmvLast30d)}`} sub="janela móvel" />
        <KpiCard icon={KPI_ICON.comissao} label="Comissão acumulada" value={`R$ ${money(d?.money.commissionAllTime)}`} sub="7,99% flat" />
        <KpiCard icon={KPI_ICON.assinaturas} label="Assinaturas ativas" value={String(d?.activeSubscriptions ?? '—')} sub="recorrência" />
        <KpiCard icon={KPI_ICON.parceiros} label="Parceiros ativos" value={String(d?.partners.active ?? '—')} sub={`${d?.partners.high ?? 0} em risco alto`} />
        <KpiCard icon={KPI_ICON.lojas} label="Lojas publicadas" value={String(d?.stores.published ?? '—')} sub={`${d?.stores.draft ?? 0} em rascunho`} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-space-lg">
        <section className="flex flex-col p-space-lg rounded-xl bg-surface-container-low shadow-md gap-space-md">
          <div className="flex items-center justify-between">
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Parceiros</h2>
            <Link to="/parceiros" className="font-label-sm text-label-sm text-primary hover:underline">
              Ver todos
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-space-sm">
            <StatChip label="Ativos" value={d?.partners.active} tone="secondary" />
            <StatChip label="Suspensos" value={d?.partners.suspended} tone="error" />
            <StatChip label="Observação" value={d?.partners.watch} tone="tertiary" />
            <StatChip label="Risco alto" value={d?.partners.high} tone="error" />
          </div>
        </section>

        <section className="flex flex-col p-space-lg rounded-xl bg-surface-container-low shadow-md gap-space-md">
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Lojas</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-space-sm">
            <StatChip label="Rascunho" value={d?.stores.draft} tone="outline" />
            <StatChip label="Publicadas" value={d?.stores.published} tone="secondary" />
            <StatChip label="Despublicadas" value={d?.stores.unpublished} tone="outline" />
            <StatChip label="Suspensas" value={d?.stores.suspended} tone="error" />
          </div>
        </section>
      </div>

      <section className="flex flex-col p-space-lg rounded-xl bg-surface-container-low shadow-md gap-space-md">
        <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Funil (últimos 30 dias)</h2>
        {!funnel ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">Carregando…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-outline font-label-sm text-label-sm uppercase tracking-wider bg-surface-container-lowest/60">
                  <th className="py-space-sm px-space-md rounded-l-lg">Etapa</th>
                  <th className="py-space-sm px-space-sm text-right">Quantidade</th>
                  <th className="py-space-sm px-space-md text-right rounded-r-lg">Conversão</th>
                </tr>
              </thead>
              <tbody className="font-body-sm text-body-sm divide-y divide-surface-container-highest/30">
                <tr>
                  <td className="py-space-sm px-space-md text-on-surface">Visualizações de página</td>
                  <td className="py-space-sm px-space-sm text-right font-mono text-on-surface">{funnel.pageViews}</td>
                  <td className="py-space-sm px-space-md text-right text-outline">—</td>
                </tr>
                <tr>
                  <td className="py-space-sm px-space-md text-on-surface">Checkouts iniciados</td>
                  <td className="py-space-sm px-space-sm text-right font-mono text-on-surface">{funnel.checkoutsStarted}</td>
                  <td className="py-space-sm px-space-md text-right text-outline">{pct(funnel.checkoutsStarted, funnel.pageViews)}</td>
                </tr>
                <tr>
                  <td className="py-space-sm px-space-md text-on-surface">Pagos via PIX</td>
                  <td className="py-space-sm px-space-sm text-right font-mono text-on-surface">{funnel.paidPix}</td>
                  <td className="py-space-sm px-space-md text-right text-outline">{pct(funnel.paidPix, funnel.checkoutsStarted)}</td>
                </tr>
                <tr>
                  <td className="py-space-sm px-space-md text-on-surface">Pagos via cartão</td>
                  <td className="py-space-sm px-space-sm text-right font-mono text-on-surface">{funnel.paidCard}</td>
                  <td className="py-space-sm px-space-md text-right text-outline">{pct(funnel.paidCard, funnel.checkoutsStarted)}</td>
                </tr>
                <tr>
                  <td className="py-space-sm px-space-md text-on-surface font-bold">Total pago</td>
                  <td className="py-space-sm px-space-sm text-right font-mono text-secondary font-bold">R$ {money((funnel.paidAmountCents ?? 0) / 100)}</td>
                  <td className="py-space-sm px-space-md text-right text-outline">{pct((funnel.paidPix ?? 0) + (funnel.paidCard ?? 0), funnel.checkoutsStarted)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-space-md">
        <DemoBanner>
          O protótipo Super Admin descreve antifraude por ML, certificação PCI-DSS/BACEN e infraestrutura (WAF/HSM) que a Storeo não tem hoje —
          pagamento roda em adapter mock. Cards abaixo são ilustrativos.
        </DemoBanner>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-space-md">
          <SignalCard icon="format_image_left" tone="secondary" label="Antifraude Cognitivo" value="99.2%" sub="Machine Learning Score < 0.08" />
          <SignalCard icon="security_update_warning" tone="tertiary" label="Índice de Chargeback" value="0.12%" sub="Teto 0.90% · Visa/Mastercard" />
          <SignalCard icon="lock" tone="primary" label="PCI-DSS Nível 1 & BACEN" value="Certificado v4.0" sub="Criptografia ponta a ponta HSM" />
        </div>
      </section>
    </>
  );
}

function KpiCard({ icon, label, value, sub }: { icon: string; label: string; value: string; sub: string }) {
  return (
    <div className="relative overflow-hidden rounded-xl bg-surface-container-low p-space-md shadow-md flex flex-col justify-between gap-space-2xs">
      <div className="flex items-center justify-between">
        <span className="font-label-sm text-label-sm uppercase tracking-wider text-outline">{label}</span>
        <MSym name={icon} className="text-[18px] text-secondary" />
      </div>
      <div className="font-headline-sm text-headline-sm text-on-surface font-bold truncate" title={value}>
        {value}
      </div>
      <span className="font-label-sm text-label-sm text-on-surface-variant">{sub}</span>
    </div>
  );
}

const TONE_CLS: Record<string, string> = {
  secondary: 'bg-secondary/15 text-secondary',
  error: 'bg-error-container/25 text-error',
  tertiary: 'bg-tertiary/15 text-tertiary',
  outline: 'bg-surface-container-highest text-outline',
  primary: 'bg-primary-container/20 text-primary',
};

function StatChip({ label, value, tone }: { label: string; value: number | undefined; tone: string }) {
  return (
    <div className={`rounded-xl p-space-sm flex flex-col gap-space-3xs ${TONE_CLS[tone]}`}>
      <span className="font-label-sm text-label-sm uppercase tracking-wider opacity-80">{label}</span>
      <span className="font-headline-sm text-headline-sm font-bold">{value ?? '—'}</span>
    </div>
  );
}

function SignalCard({ icon, tone, label, value, sub }: { icon: string; tone: string; label: string; value: string; sub: string }) {
  return (
    <div className="p-space-md rounded-xl bg-surface-container-low shadow-md flex items-center justify-between gap-space-sm">
      <div className="flex items-center gap-space-sm min-w-0">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${TONE_CLS[tone]}`}>
          <MSym name={icon} className="text-[28px]" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="font-label-sm text-label-sm uppercase text-outline flex items-center gap-1">
            {label} <PlaceholderTag />
          </span>
          <div className="font-headline-sm text-headline-sm text-on-surface font-bold truncate">{value}</div>
          <span className="text-[11px] text-on-surface-variant truncate">{sub}</span>
        </div>
      </div>
    </div>
  );
}
